import { Icon, cn } from "@sagot/ui";
import type { ArticleCard as ArticleCardData } from "../workflow/article";
import { DEFAULT_ARTICLE_LABEL, resolveWorkflowLabels, type WorkflowCardLabels } from "../workflow/labels";
import { useChatTheme } from "./chat-theme";

const LINK = "inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-pill border px-4 py-2 text-left text-sm font-semibold leading-snug";

export interface ArticleCardProps {
  card: ArticleCardData;
  labels?: Partial<WorkflowCardLabels>;
}

/**
 * The article card of a workflow (`show_article`): the page's title, the author's line and a button that opens the link in a new
 * tab. Nothing to answer, so it stays the same after the visitor moves on. The link is read by assistive technology as
 * "<label>, opens in a new tab"; only https links reach this component (the decoder drops the rest).
 */
export function ArticleCard({ card, labels: overrides }: ArticleCardProps) {
  const labels = resolveWorkflowLabels(overrides);
  const { themed } = useChatTheme();
  const { article } = card;
  const label = article.label ?? DEFAULT_ARTICLE_LABEL;
  return (
    <section aria-label={labels.articleGroup(article.title)} className="mt-2 rounded-lg border border-line bg-surface p-3" data-testid="article-card">
      <p className="flex items-start gap-2 text-sm font-semibold text-fg">
        <Icon name="book-open" className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
        <span className="min-w-0 break-words">{article.title}</span>
      </p>
      {article.text ? <p className="mt-1 break-words text-xs text-fg-muted">{article.text}</p> : null}
      <a
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(LINK, "mt-3", themed ? "border-[var(--wg-primary)] bg-surface text-fg hover:bg-sunken" : "border-primary bg-surface text-primary-ink hover:bg-primary-soft")}
      >
        <span className="min-w-0 break-words">{label}</span>
        <Icon name="external-link" className="size-4 shrink-0" />
        <span className="sr-only">{labels.articleNewTab}</span>
      </a>
    </section>
  );
}
